/**
 * Mirrors of the `getTrackingPageData` payload.
 *
 * The API merges the merchant's stored config over server-side defaults before
 * returning it, so every field here is present — the page never has to supply
 * its own fallbacks.
 */

export type TrackingPageFont = 'system' | 'hanken' | 'serif' | 'mono';
export type TrackingPageCornerRadius = 'sharp' | 'rounded' | 'pill';
export type TrackingPageAdPlacement = 'above_timeline' | 'below_timeline' | 'below_items';
export type TrackingPageCoverPosition = 'left' | 'right';

export type TrackingPageAd = {
  id: string;
  /** Shopify product this snapshot came from, when picked rather than hand-built. */
  productId: string | null;
  imageUrl: string;
  headline: string | null;
  body: string | null;
  ctaLabel: string | null;
  ctaUrl: string;
  placement: TrackingPageAdPlacement;
};

export type TrackingPageConfig = {
  version: number;
  theme: {
    primaryColor: string;
    backgroundColor: string;
    surfaceColor: string;
    textColor: string;
    fontFamily: TrackingPageFont;
    cornerRadius: TrackingPageCornerRadius;
  };
  branding: {
    logoUrl: string | null;
    logoHeight: number;
    coverImageUrl: string | null;
    coverImagePosition: TrackingPageCoverPosition;
    showPoweredBy: boolean;
  };
  sections: {
    hero: boolean;
    progress: boolean;
    timeline: boolean;
    items: boolean;
    ads: boolean;
    support: boolean;
  };
  copy: {
    headline: string | null;
    supportEmail: string | null;
    supportUrl: string | null;
  };
  ads: TrackingPageAd[];
};

/** Mirrors `Tracker_Event_Types_Enum` plus the `UNKNOWN` pseudo-status. */
export type TrackingStatus =
  | 'PRE_TRANSIT'
  | 'IN_TRANSIT'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'AVAILABLE_FOR_PICKUP'
  | 'RETURN_TO_SENDER'
  | 'FAILURE'
  | 'CANCELLED'
  | 'ERROR'
  | 'UNKNOWN';

export type StatusVariant = 'normal' | 'pickup' | 'exception' | 'cancelled' | 'unknown';
export type StatusTone = 'neutral' | 'progress' | 'success' | 'warning' | 'danger';

export type TrackingCheckpoint = {
  message: string;
  description: string | null;
  address: string | null;
  time: string | null;
};

export type TrackingCheckpointGroup = {
  date: string;
  checkpoints: TrackingCheckpoint[];
};

export type TrackingItem = {
  title: string;
  quantity: number;
  price: number | null;
  imageUrl: string | null;
};

export type TrackingShipment = {
  trackingNumber: string;
  trackingCompany: string;
  carrierTrackingUrl: string | null;
  status: TrackingStatus;
  statusLabel: string;
  statusVariant: StatusVariant;
  statusTone: StatusTone;
  /** Index into the four-node progress rail. */
  nodeIndex: number;
  estimatedDeliveryDate: string | null;
  checkpoints: TrackingCheckpointGroup[];
  items: TrackingItem[];
};

export type TrackingPageData = {
  storeId: string | null;
  storeName: string | null;
  orderNumber: string | null;
  recipientName: string | null;
  branding: TrackingPageConfig;
  shipments: TrackingShipment[];
};

/** The four nodes of the happy-path rail, in order. */
export const RAIL_NODES: { status: TrackingStatus; label: string }[] = [
  { status: 'PRE_TRANSIT', label: 'Ordered' },
  { status: 'IN_TRANSIT', label: 'In transit' },
  { status: 'OUT_FOR_DELIVERY', label: 'Out for delivery' },
  { status: 'DELIVERED', label: 'Delivered' },
];
